import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Injector,
    Input,
    OnDestroy,
    Optional,
    Output,
    QueryList,
    Type,
    ViewChildren,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
    FocusSelectionParams,
    InteractionEntity,
} from '../../coaching-base/config';
import {
    FocusDto,
    FocusMetricDto,
    NoteCategory,
} from '../../coaching-base/general';
import { CoachingSwitchboardService } from '../../coaching-base/switchboard';
import { NoteDetailFormValue } from '../../coaching-note/note-detail-form-value.model';
import { createNoteDetail } from '../../coaching-note/note-detail.form';
import { BehaviourItem } from '../../coaching-tags/models/behaviour.coaching.model';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { SubBehaviourItem } from '../../coaching-tags/models/subbehaviour.coaching.model';
import { TagsDialogResult } from '../../coaching-tags/tags-dialog-result.model';
import { TagsComponent } from '../../coaching-tags/tags/tags.component';
import { dedup } from '../../coaching-tags/tags/tags.function';
import {
    composeTagIdentifierNext,
    FocusShallow,
    TagsDialogData,
    TagSetMode,
} from '../../coaching-tags/tags/tags.model';
import { FocusSelectorComponent } from '../../coaching/focus-selector/focus-selector.component';
import { FocusSelectorDialogData } from '../../coaching/focus-selector/model/focus-selector-dialog-data.model';
import { FocusSelectorDialogResult } from '../../coaching/focus-selector/model/focus-selector-dialog-result.model';
import { FocusSelectorTriggerSource } from '../../coaching/focus-selector/model/focus-selector-trigger-source.enum';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { pluck } from '../../store.decorator';
import { BringIntoFocusService } from '../bring-into-focus.service';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import {
    FOCUS_CONTAINERS_TRACKING_ID,
    ObservationFormDecorateConnector,
} from '../form-decorator/observation-form-decorate-connector';
import { ObservationFormDecoratorService } from '../form-decorator/observation-form-decorator.service';
import { ObservationCommitmentUpsertEvent } from '../observation-commitment-upsert-event';
import { createInteraction } from '../observation-interaction/interaction.observation.form';
import { TagGroupFormValue } from '../observation-tag-group/tag-group-form-value.model';
import { TagGroupObservationComponent } from '../observation-tag-group/tag-group.observation.component';
import { createFocusContainer } from '../observation-tag-group/tag-group.observation.form';
import { ObservationExtraOptions } from '../observation/observation-extra-options.model';
import {
    AllowedActions,
    ObservationFormDisplayProperties,
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from '../observation/observation.model';
import { ObservationValidationResult } from '../observation/validation/observation-validation-result.model';

@Component({
    selector: '[cmpsObservationTagGroupsNext]',
    templateUrl: './tag-groups.observation.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagGroupsObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TagGroupsObservationComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class TagGroupsObservationComponent
    implements
        AfterViewInit,
        OnDestroy,
        ControlValueAccessor,
        Validator,
        TakeUntilDestroy,
        ObservationFormDecorateConnector
{
    @Input() storePathPrefix: string;

    @ViewChildren(TagGroupObservationComponent)
    readonly items: QueryList<TagGroupObservationComponent>;

    /** @private */ _i18n = TranslationKeyMap;
    @Input() observationId: number;
    @Input() published: boolean;
    @Input() performerId: number;
    @Input() focusSelectionParams: FocusSelectionParams;
    @Input() newFocusSelectorWithYield: boolean;
    @Input() interactionEntity: InteractionEntity;
    @Input() defaultInteractionTypeId: number;
    @Input() defaultNoteCategory: NoteCategory;
    @Input() allowedActions: AllowedActions;
    @Input() behaviours: BehaviourItem[];
    @Input() subBehaviours: SubBehaviourItem[];
    @Input() metrics: MetricItem[];
    @Input() displayProperties: ObservationFormDisplayProperties;
    @Input() filteredFocus: FocusShallow[];
    @Input() isGeneric: boolean;
    @Input() isFromCommitment: boolean; // TODO revise. can possibly be removed
    @Input() activeMetric: number;
    @Output() upsertCommitment: EventEmitter<ObservationCommitmentUpsertEvent>;
    /** @private */ _form: FormArray;
    /** @private */ _untaggedTagGroupIsVisible: boolean;
    /** @private */ _isCollapsedState: boolean[];
    private _blackout: boolean;
    private _didFirstLoad = false;
    private _onChange: (_: TagGroupFormValue[]) => void;
    private _onTouched: () => void;
    private _isDisabled: boolean;
    private _onValidatorChange: () => void;
    private _dialogRef: CmpsDialogRef<
        FocusSelectorComponent | TagsComponent,
        FocusSelectorDialogResult | TagsDialogResult
    >;

    @Output() untaggedTagGroupIsVisible: EventEmitter<boolean>;
    @Input() extraOptions: ObservationExtraOptions;

    /**
     * @private
     * when 'ready' is emitted from pillar 2
     */
    _siblingPillarReady: boolean;

    /** @private */ get _value(): TagGroupFormValue[] {
        return this._form.value;
    }

    constructor(
        private readonly _injector: Injector,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        private readonly _snackBar: CmpsSnackBar,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _formDecoratorService: ObservationFormDecoratorService,
        @Optional() private readonly _focusService: BringIntoFocusService,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
    ) {
        this.upsertCommitment = new EventEmitter();
        this.untaggedTagGroupIsVisible = new EventEmitter();
    }

    ngOnInit(): void {
        this._form = new FormArray([]);

        this._form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                tap((val) => {
                    CMPS_APP_DEF.ENV !== 'prod' &&
                        console.log('_onChange tag-groups');
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(val);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._watchForSiblingPillar();
    }

    ngAfterViewInit(): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const run = () => {
            this.items.forEach((e, i) => {
                (e as any).onRemove(() => this.removeFocusContainer(i));
            });
        };
        this.items.changes
            .pipe(
                tap(() => {
                    run();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
        run();
    }

    ngOnDestroy(): void {
        this._formDecoratorService.unRegister(this);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._dialogRef && this._dialogRef.close();
        this._form && (this._form as any).clear2();
        this.upsertCommitment.complete();
        this.untaggedTagGroupIsVisible.complete();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
        this._isDisabled
            ? this._form.disable({ emitEvent: false })
            : this._form.enable({ emitEvent: false });
    }

    writeValue(obj: TagGroupFormValue[]): void {
        this._patchForm(obj);
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        return this._form.errors;
    }

    fdcDecorate(result: ObservationValidationResult): void {
        const els = Array.from(
            this._el.nativeElement.querySelectorAll('[data-focusTrackingId]'),
        );
        if (result.highlightAllFocusContainers) {
            els.forEach((e) => e.classList.add('outline_fc'));
        } else {
            els.forEach((e) => e.classList.remove('outline_fc'));
        }
    }

    fdcDecorateDelta(
        result: ObservationValidationResult,
        deltaResult: ObservationValidationResult,
    ): void {
        this.fdcDecorate(deltaResult);
    }

    fdcUnDecorate(result: ObservationValidationResult): void {
        const els = Array.from(
            this._el.nativeElement.querySelectorAll('[data-focusTrackingId]'),
        );
        els.forEach((e) => e.classList.remove('outline_fc'));
    }

    /** @private */
    _onSelectFocus(trackingId: number): void {
        this._selectFocus(trackingId, true);
    }

    /** @private */
    _onCopyNotes(trackingId: number): void {
        this._selectFocus(trackingId, false);
    }

    private _selectFocus(trackingId: number, withCurrentFocus: boolean): void {
        const tg = this._value.find((e) => e.trackingId === trackingId);
        if (!tg) {
            return null;
        }

        const selectedFocus: FocusDto = !withCurrentFocus
            ? null
            : {
                  focusId: tg.focusId,
                  userId: tg.userId,
                  behaviourId: tg.behaviourId,
                  subBehaviourId: tg.subBehaviourId,
                  metrics: tg.metrics
                      ? tg.metrics
                            .filter((e) => !e.isActiveGoal)
                            .map((e) => e.metricId)
                      : [],
              };

        const pinnedMetrics = [];
        if (!this.isGeneric && this.activeMetric != null) {
            pinnedMetrics.push(this.activeMetric);
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._dialogRef && this._dialogRef.close();

        // TODO need to fix click event leak on stacked uikit dialogs
        let isCheckingCanSelectFocus = false;
        const data: FocusSelectorDialogData | TagsDialogData = {
            storePathPrefix: this.storePathPrefix,
            mode: TagSetMode.DEFAULT,
            focusSelectionParams: this.focusSelectionParams,
            commitmentId: null,
            performerId: this.performerId,
            selected: selectedFocus,
            pinnedMetrics,
            isGeneric: this.isGeneric,
            activeMetricId: this.isGeneric ? null : this.activeMetric,
            triggerSource: FocusSelectorTriggerSource.OBSERVATION,
            canSelectFocus: (focus) => {
                isCheckingCanSelectFocus = true;
                const ret = this._canSelectFocus(focus);
                if (ret instanceof Promise) {
                    return ret.then((result) => {
                        isCheckingCanSelectFocus = false;
                        return result;
                    });
                }
                isCheckingCanSelectFocus = false;
                return ret;
            },
        };

        const dialogComponent: Type<FocusSelectorComponent | TagsComponent> =
            this.newFocusSelectorWithYield
                ? FocusSelectorComponent
                : TagsComponent;

        this._dialogRef = this._dialog.open(dialogComponent, {
            resizable: true,
            movable: true,
            injector: this._injector,
            canClose: () => !isCheckingCanSelectFocus,
            data,
        });
        this._dialogRef.result
            .pipe(
                filter((ret) => !!ret),
                tap((ret) => {
                    this._setFocus(trackingId, ret.selected);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _canSelectFocus(focus: FocusDto): boolean | Promise<boolean> {
        // simple pre-check
        const found =
            this._value.findIndex(
                (e) =>
                    e.behaviourId === focus.behaviourId &&
                    e.subBehaviourId === focus.subBehaviourId,
            ) !== -1;
        if (!found) {
            return true;
        }

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9650_THIS_FOCUS_ALREADY_EXISTS__DO_YOU_WANT_TO_MOVE_THE_ITEMS_TO_THIS_FOCUS_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        return ref.result
            .pipe(
                map((ret) => {
                    return ret === 0;
                }),
                takeUntilDestroy(this),
            )
            .toPromise();
    }

    private _setFocus(trackingId: number, focus: FocusDto): void {
        // simple pre-check
        const found =
            this._value.findIndex(
                (e) =>
                    e.behaviourId === focus.behaviourId &&
                    e.subBehaviourId === focus.subBehaviourId,
            ) !== -1;

        this._applyFocus(trackingId, focus);

        if (!found) {
            this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                duration: 5000,
                placement: 'bottom-right',
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                data: {
                    text: this._translateService.instant(
                        TranslationKeyMap._9651_A_NEW_FOCUS_CONTAINER_HAS_BEEN_CREATED_WITH_YOUR_FOCUS_SELECTION,
                    ),
                } as CmpsTextOnlySnackBarOptions,
            });
        }
    }

    removeFocusContainer(index: number): void {
        const wasNonFocusCtr = this._value[index].behaviourId == null;
        this._form.removeAt(index);
        this._isCollapsedState.splice(index, 1);
        if (wasNonFocusCtr) {
            // ensure the non-focus container exists
            this._createFocusContainer(false);
        }
    }

    private _copyNotes(trackingId: number, focus: FocusDto): void {
        const next = this._value ? [...this._value] : [];

        const tg = next.find((e) => e.trackingId === trackingId);
        if (!tg) {
            return;
        }

        const notes = pluck<NoteDetailFormValue[]>(tg.interactions, 'notes[]');

        const newIcn = createInteraction(this.defaultInteractionTypeId);
        newIcn.notes = notes.map((e) => {
            const item = createNoteDetail(
                e.category != null ? e.category : this.defaultNoteCategory,
            );
            item.contents = e.contents;
            return item;
        });

        const identifier = composeTagIdentifierNext(focus);
        const toCtrIndex = next.findIndex(
            (e) => composeTagIdentifierNext(e) === identifier,
        );
        let toCtrCtrl: FormControl;
        if (toCtrIndex === -1) {
            const newCtr = createFocusContainer(focus);
            newCtr.interactions = [];
            toCtrCtrl = this._formBuilder.control(newCtr);
        } else {
            toCtrCtrl = this._form.controls[toCtrIndex] as FormControl;
        }

        const toCtrValue = toCtrCtrl.value as TagGroupFormValue;
        toCtrCtrl.patchValue({
            ...toCtrValue,
            interactions: [...toCtrValue.interactions, newIcn],
        });
        // TODO revise sorting
        if (toCtrIndex === -1) {
            this._isCollapsedState.splice(0, 0, false);
            this._form.insert(0, toCtrCtrl);
        } else {
            this._isCollapsedState[toCtrIndex] = false;
        }
    }

    onCreateFocusContainerClick(): void {
        this._createFocusContainer(true);
    }

    private IsFiltered(focus: TagGroupFormValue): boolean {
        if (!this.filteredFocus) {
            return true;
        }

        return this.filteredFocus.some(
            (x) =>
                x.subBehaviourId === focus.subBehaviourId &&
                x.behaviourId === focus.behaviourId,
        );
    }

    private _patchForm(
        value?: TagGroupFormValue[],
        externalChange?: boolean,
    ): void {
        this._blackout = true;

        const next: TagGroupFormValue[] = (value || []).filter((v) =>
            this.IsFiltered(v),
        );

        this._checkNonFocusCtrIsVisible(next);

        if (!this._didFirstLoad) {
            this._didFirstLoad = true;
            this._isCollapsedState = new Array(next.length).fill(true);
            if (this._isCollapsedState.length !== 0) {
                this._isCollapsedState[0] = false;
            }
        }

        (this._form as any).clear2();
        (this._form as any).push2(
            ...next.map((e) => this._formBuilder.control(e)),
        );

        if (!value) {
            this._form.reset(next, { emitEvent: false });
        } else {
            this._form.patchValue(next, { emitEvent: false });
        }

        if (this._isDisabled) {
            this._form.disable({ emitEvent: false });
        }

        externalChange && this._onChange && this._onChange(next);

        this._formDecoratorService.unRegister(this);
        this._formDecoratorService.registerForTrackingId(
            FOCUS_CONTAINERS_TRACKING_ID,
            this,
        );

        this._blackout = false;
    }

    private _createFocusContainer(visible: boolean): void {
        const index = this._value.findIndex((e) => e.behaviourId == null);

        // fc with default interaction id
        const focusCont = createFocusContainer(
            {
                focusId: null,
                userId: null,
                behaviourId: null,
                subBehaviourId: null,
                metrics: [],
            },
            this.defaultInteractionTypeId,
        );
        if (index === -1) {
            const ctrl = this._formBuilder.control(focusCont);
            this._form.insert(0, ctrl);
            this._isCollapsedState.splice(0, 0, false);
        } else if (index === 1 && visible) {
            this._form.removeAt(index);
            this._isCollapsedState.splice(index, 1);
            const ctrl = this._formBuilder.control(focusCont);
            this._form.insert(0, ctrl);
            this._isCollapsedState.splice(0, 0, !visible);
        }
        const prev = this._untaggedTagGroupIsVisible;
        this._untaggedTagGroupIsVisible = visible;
        if (prev !== this._untaggedTagGroupIsVisible) {
            this.untaggedTagGroupIsVisible.emit(
                this._untaggedTagGroupIsVisible,
            );
        }
        if (visible) {
            this._focusFirstContainer();
        }
    }

    // focus first container
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _focusFirstContainer() {
        if (this._focusService) {
            const selectorfc = `[id='${this._value[0].trackingId}']`;
            this._focusService.registerSelector(selectorfc, () => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                const el = document.querySelector(selectorfc) as HTMLElement;
                if (el) {
                    el.focus();
                }
            });
        }
    }

    /** emits the state of visibility of the presence of a non focus container */
    private _checkNonFocusCtrIsVisible(val: TagGroupFormValue[]): void {
        const prev = this._untaggedTagGroupIsVisible;

        const hasFocusCtrs = val.findIndex((e) => e.behaviourId != null) !== -1;
        const emptyCtr = val.find((e) => e.behaviourId == null);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const emptyCtrHasMetrics =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            emptyCtr && emptyCtr.metrics && emptyCtr.metrics.length !== 0;

        this._untaggedTagGroupIsVisible =
            (!hasFocusCtrs && emptyCtr != null) ||
            (!this._didFirstLoad &&
                (this.isGeneric || emptyCtrHasMetrics) &&
                !this.isFromCommitment);

        if (prev !== this._untaggedTagGroupIsVisible) {
            this.untaggedTagGroupIsVisible.emit(
                this._untaggedTagGroupIsVisible,
            );
        }
    }

    private _applyFocus(trackingId: number, focus: FocusDto): void {
        const fromCtrIndex = this._value.findIndex(
            (e) => e.trackingId === trackingId,
        );
        if (fromCtrIndex === -1) {
            return;
        }

        const fromCtrCtrl = this._form.controls[fromCtrIndex] as FormControl;
        const fromCtrValue = fromCtrCtrl.value as TagGroupFormValue;
        const identifier = composeTagIdentifierNext(focus);
        const toCtrIndex = this._value.findIndex(
            (e) => composeTagIdentifierNext(e) === identifier,
        );
        const newCtr = createFocusContainer(focus); // just using this to get the transformed focus metrics

        const mergeMetrics = (
            nextMetrics: FocusMetricDto[],
        ): FocusMetricDto[] => {
            const nextGoalMetrics = dedup<FocusMetricDto>(
                nextMetrics.filter((e) => e.isActiveGoal),
                (e) => e.metricId,
            );
            const nextGoalMetricIds = nextGoalMetrics.map((e) => e.metricId);
            // TODO revise. this is only keeping metrics that are not already goals
            const nextFocusMetrics = dedup<FocusMetricDto>(
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                nextMetrics.filter(
                    (e) =>
                        !e.isActiveGoal &&
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        nextGoalMetricIds.indexOf(e.metricId) === -1,
                ),
                (e) => e.metricId,
            );
            return [...nextGoalMetrics, ...nextFocusMetrics];
        };

        if (fromCtrIndex === toCtrIndex || toCtrIndex === -1) {
            const nextMetrics = [
                ...newCtr.metrics,
                ...(fromCtrValue.metrics || []),
            ];
            const mergedMetrics = mergeMetrics(nextMetrics);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            fromCtrCtrl.patchValue({
                ...fromCtrValue,
                trackingChanged: true,
                focusId: newCtr.focusId,
                userId: newCtr.userId,
                behaviourId: newCtr.behaviourId,
                subBehaviourId: newCtr.subBehaviourId,
                metrics: mergedMetrics,
            } as TagGroupFormValue);

            this._isCollapsedState[fromCtrIndex] = false;
        } else {
            const toCtrCtrl = this._form.controls[toCtrIndex] as FormControl;
            const toCtrValue = toCtrCtrl.value as TagGroupFormValue;

            /* remove this first to maybe prevent side effects of
            fromCtrValue.interactions and fromCtrValue.metrics still being present in a child form */
            this._form.removeAt(fromCtrIndex);
            this._isCollapsedState[toCtrIndex] = false;
            this._isCollapsedState.splice(fromCtrIndex, 1);

            const nextMetrics = [
                ...newCtr.metrics,
                ...(fromCtrValue.metrics || []),
                ...(toCtrValue.metrics || []),
            ];
            const mergedMetrics = mergeMetrics(nextMetrics);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            toCtrCtrl.patchValue({
                ...toCtrValue,
                trackingChanged: true,
                interactions: [
                    ...(fromCtrValue.interactions || []),
                    ...(toCtrValue.interactions || []),
                ],
                metrics: mergedMetrics,
            } as TagGroupFormValue);
        }

        this._checkNonFocusCtrIsVisible(this._value);
    }

    /** this is mainly a guard for child focus containers, so that menu options can be determined.
     * this is done here, so that only one subscription is needed */
    private _watchForSiblingPillar(): void {
        (!this._switchboardService.pillarTwo.connected()
            ? this._switchboardService.observableForConnect().pipe(
                  filter(() => this._switchboardService.pillarTwo.connected()),
                  take(1),
              )
            : of(null)
        )
            .pipe(
                switchMap(() => this._switchboardService.pillarTwo.sbReady()),
                filter((val) => val === true),
                take(1),
                tap(() => {
                    this._siblingPillarReady = true;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
